// Tipos e regras de itens usados pelo servidor e pelo navegador.
import { ITEM_REGEN } from './itemregen.js';
import { describeAffix, type Affix } from './affixes.js';

export const EQUIP_SLOTS = ['head', 'neck', 'back', 'weapon', 'armor', 'shield', 'ring', 'legs', 'ammo', 'feet'] as const;
export type EquipSlot = (typeof EQUIP_SLOTS)[number];

/** Atributos de um tipo de item (gerados de appearances.json + TibiaWiki por tools/build_items.py). */
export interface ItemType {
  id: number;
  name: string;
  article: 'a' | 'an';
  slot?: EquipSlot;
  twoHanded?: boolean;
  stackable?: boolean;
  /** Quantos itens cabem, se for um recipiente (mochila, bolsa). */
  container?: number;
  weight: number;
  attack?: number;
  defense?: number;
  defenseMod?: number;
  armor?: number;
  elementAttack?: string;
  /** proteção por elemento em % (oficial, items.xml do Canary); negativo = fraqueza */
  absorb?: Record<string, number>;
  /** tipo oficial da arma: sword, axe, club, distance, wand, shield, ammunition... (decide a skill treinada) */
  weaponType?: string;
  /** maior preço que um NPC do Global paga pelo item */
  npcPrice?: number;
  /** item de loot sem preço (munição, comida...): o NPC paga isto por unidade */
  minSell?: number;
  /** bônus de skill e de magic level quando equipado */
  skillBonus?: Record<string, number>;
  damage?: string;
  damageType?: string;
  /** dados oficiais (items.xml do Canary): mana por tiro de wand/rod, chance de acerto, munição, velocidade, defesa extra,
   * cargas, duração (s) e o item em que ele vira ao ser equipado (anéis) */
  mana?: number; hitChance?: number; maxHitChance?: number; ammoType?: string; speed?: number; extraDef?: number;
  charges?: number; duration?: number; equipTo?: number;
  /** ao acabar a duração vira este item (Canary decayTo: pair of soft boots → worn soft boots) */
  decayTo?: number;
  range?: number;
  level?: number;
  vocations?: string[];
  skills?: string;
  value?: number;
  market?: number;
  tierClass?: number;
  /** árvore de Proficiência de Armas (id do proficiencies.json) */
  prof?: number;
  /** espaços de imbuement e o grau máximo de cada categoria (oficial) */
  imbueSlots?: number; imbueAllowed?: Record<string, number>;
}

export type Rarity = 0 | 1 | 2 | 3;
export const RARITY_NAMES = ['Comum', 'Raro', 'Épico', 'Lendário'] as const;

/** pouch = Supply Pouch (poções e runas; só saem gastas ou destruídas) */
export type ItemPlace = 'equip' | 'container' | 'depot' | 'inbox' | 'pouch';

/** Um item que pertence a um personagem. */
export interface ItemInstance {
  id: number;
  typeId: number;
  count: number;
  rarity: Rarity;
  place: ItemPlace;
  /** Nome do slot de equipamento ou posição dentro do recipiente/depot. */
  slot: string;
  parentId: number | null;
  /** Afixos de raridade (raro, épico e lendário). */
  affixes?: Affix[];
  /** Upgrade (+1 a +30): arma +2% de dano por nível; armaduras e escudos +1 de armadura/defesa a cada 2 níveis. */
  upgrade?: number;
  /** Usos restantes (itens de treino). */
  charges?: number;
  /** Item da loja/recompensa: só o dono usa; não pode ser jogado fora, vendido nem negociado. */
  bound?: boolean;
  /** tier da Exaltation Forge (0 a 10) */
  tier?: number;
  /** Imbuements aplicados (um por espaço; left = segundos de uso restantes) */
  imbues?: Array<{ name: string; tier: number; left: number } | null>;
}

export const STORE_INBOX_SIZE = 100;

/** Bônus do upgrade de um item (proposta Baiak). */
export const upgradeDamagePct = (up: number | undefined) => (up ?? 0) * 2;
/** +1 de armadura (peças) ou de defesa (escudo) por nível de refine: +30 no +30 (decisão do Arthur, 15/10) */
export const upgradeArmor = (up: number | undefined) => up ?? 0;
/** +1 de ataque por nível de refine na arma e na munição: +30 no +30 (decisão do Arthur, 15/10) */
export const upgradeAttack = (up: number | undefined) => up ?? 0;
/** itens de defesa (capacete, armadura, pernas, botas e escudo): +0,5% de proteção contra todos os elementos por nível (aprovado pelo Arthur em 13/10) */
export const DEFENSE_SLOTS = ['head', 'armor', 'legs', 'feet', 'shield'];
export const upgradeProtection = (up: number | undefined) => (up ?? 0) * 0.5;
/** texto do bônus do refine para um item */
export function upgradeText(slot: string | undefined, up: number): string {
  if (slot === 'weapon' || slot === 'ammo') return `+${upgradeAttack(up)} de ataque e +${upgradeDamagePct(up)}% de dano`;
  const prot = String(upgradeProtection(up)).replace('.', ',');
  return `+${upgradeArmor(up)} de ${slot === 'shield' ? 'defesa' : 'armadura'} e +${prot}% de proteção contra todos os elementos`;
}

export type MoveTarget =
  | { place: 'equip'; slot: EquipSlot }
  | { place: 'container'; parentId: number; slot?: number }
  | { place: 'depot'; slot?: number }
  /** de volta para a Store Box: só itens presos ao personagem e as fichas da roleta */
  | { place: 'inbox'; slot?: number }
  /** Supply Pouch: só suprimentos (poções e runas) */
  | { place: 'pouch'; slot?: number };

export const MAX_STACK = 100;
export const DEPOT_SIZE = 500;
/** Mochilas e bolsas não têm limite de espaço (regra do Arthur): o limite é a capacidade, como no Global. */
export const BAG_SLOTS = 100_000;

/** Índice do sprite de um empilhável conforme a quantidade (moedas mudam de desenho). */
export function stackSpriteIndex(count: number, variants: number): number {
  if (variants < 8) return Math.min(variants - 1, Math.max(0, count - 1));
  if (count < 5) return count - 1;
  if (count < 10) return 4;
  if (count < 25) return 5;
  if (count < 50) return 6;
  return 7;
}

import { tierChance } from './forge.js';
const pctTxt = (v: number) => String(Math.round(v * 100) / 100).replace('.', ',');
const plural = (name: string) => (/(s|sh|ch|x)$/.test(name) ? name + 'es' : name + 's');

/** Texto de "Look" no formato do cliente do Tibia. */
const SKILL_LABEL: Record<string, string> = { sword: 'sword fighting', axe: 'axe fighting', club: 'club fighting', distance: 'distance fighting', shielding: 'shielding', fist: 'fist fighting', magic: 'magic level',
  mldeath: 'death magic level', mlfire: 'fire magic level', mlice: 'ice magic level', mlearth: 'earth magic level', mlenergy: 'energy magic level', mlholy: 'holy magic level', mlhealing: 'healing magic level' };

export function describeItem(t: ItemType, item: Pick<ItemInstance, 'count' | 'rarity' | 'affixes' | 'upgrade' | 'charges'>): string {
  const name = t.name.toLowerCase() + (item.upgrade ? ` +${item.upgrade}` : '');
  const head = item.count > 1 ? `${item.count} ${plural(name)}` : `${t.article} ${name}`;
  const stats: string[] = [];
  if (t.range !== undefined && t.slot === 'weapon' && t.damage === undefined) stats.push(`Range:${t.range}`);
  if (t.attack !== undefined) stats.push(`Atk:${t.attack}${t.elementAttack ? ` ${t.elementAttack}` : ''}`);
  if (t.defense !== undefined) stats.push(`Def:${t.defense}${t.defenseMod ? ` +${t.defenseMod}` : ''}`);
  if (t.armor !== undefined) stats.push(`Arm:${t.armor}`);
  if (t.skillBonus) stats.push(Object.entries(t.skillBonus).map(([k, v]) => `${SKILL_LABEL[k] ?? k} ${v > 0 ? '+' : ''}${v}`).join(', '));
  else if (t.skills) stats.push(t.skills);
  if (t.absorb) stats.push('protection ' + Object.entries(t.absorb).map(([k, v]) => `${k} ${v > 0 ? '+' : ''}${v}%`).join(', '));
  let text = `You see ${head}${stats.length ? ` (${stats.join(', ')})` : ''}.`;
  if (t.container) text += ` (Vol:${t.container})`;
  if (t.level || t.vocations?.length) text += `\nIt can only be wielded properly by ${t.vocations?.length ? t.vocations.map((v) => v + 's').join(' and ') : 'players'}${t.level ? ` of level ${t.level} or higher` : ''}.`;
  if ((item as ItemInstance).tier) {
    const tr = (item as ItemInstance).tier!;
    const eff = t.slot === 'weapon' ? ['Onslaught', 'onslaught'] : t.slot === 'armor' ? ['Ruse', 'ruse'] : t.slot === 'head' ? ['Momentum', 'momentum'] : t.slot === 'legs' ? ['Transcendence', 'transcendence'] : t.slot === 'feet' ? ['Amplification', 'amplification'] : null;
    text += `\nTier ${tr}${eff ? ` (${eff[0]} ${pctTxt(tierChance(eff[1] as any, tr))}%)` : ''}.`;
  }
  if (item.rarity) text += `\nRaridade: ${RARITY_NAMES[item.rarity]}${item.affixes?.length ? ` (${item.affixes.map(describeAffix).join(', ')})` : ''}.`;
  if ((item as ItemInstance).bound) text += `\nItem da loja: não pode ser jogado fora, vendido nem negociado.`;
  if (item.charges !== undefined) text += `\nIt has ${item.charges} charges left.`;
  const ims = (item as ItemInstance).imbues;
  if (t.imbueSlots) text += `\nImbuements: ${Array.from({ length: t.imbueSlots }, (_, i) => { const x = ims?.[i]; return x && x.left > 0 ? `${['', 'Basic', 'Intricate', 'Powerful'][x.tier]} ${x.name} ${Math.floor(x.left / 3600)}:${String(Math.floor((x.left % 3600) / 60)).padStart(2, '0')}h` : 'Empty Slot'; }).join(', ')}.`;
  const rg = ITEM_REGEN[t.id];
  if (rg) text += `\nEquipado: regenera ${rg.hp} de vida e ${rg.mana} de mana a cada ${rg.hpTicks / 1000} s.`;
  if (item.upgrade) text += `\nRefine +${item.upgrade}: ${upgradeText(t.slot, item.upgrade)}.`;
  text += `\nIt weighs ${(t.weight * item.count).toFixed(2)} oz.`;
  return text;
}

/** Venda ao NPC: item comum (sem raridade) no preço normal (o 2x de 14/10 foi revertido pelo Arthur no mesmo dia);
 *  raro, épico e lendário seguem +50% por nível. Itens que o próprio NPC vende ficam no preço normal. */
export const COMMON_SELL_MULT = 1;
export function npcSellUnit(base: number, rarity: number, npcSells: boolean, minSell = 0): number {
  if (!base) return minSell;   // munição e outros itens de loot sem preço: 1 gp
  if (rarity > 0) return Math.floor(base * (1 + rarity * 0.5));
  return npcSells ? base : base * COMMON_SELL_MULT;
}
